import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { FRONTEND_BUILD, isBuildSetting } from './app/shared/services/frontend-build';
import { showEnvironmentLoadError } from './utils/environment-error-display.util';

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function getPlanningToolApiRoot(): string {
  return ((environment as any).planningToolApiEndpointAddress || '').replace(/\/pt4\/?$/, '');
}

// Load runtime configuration before bootstrapping Angular
async function loadRuntimeConfiguration(): Promise<void> {
  try {
    const endpointUrl = environment.tmsApiEndpointAddress + "environment";
    const response = await fetch(endpointUrl);
    
    if (response.ok) {
      try {
      const data = await response.json();
      // Merge runtime configuration into environment object
      for (const key in data) {
        if (isBuildSetting(key)) { continue; }
        const value = data[key];
        if (value !== null && value !== undefined) {
          if (typeof environment[key] === 'object' && typeof data[key] === 'object' && !Array.isArray(data[key])) {
            // Deep merge for objects
            environment[key] = Object.assign({}, environment[key] || {}, data[key]);
          } else {
            // Direct assignment for primitives
            environment[key] = data[key];
          }
        }
      }
      console.log('Runtime configuration loaded successfully');
      } catch (jsonError) {
        const errorMsg = 'Failed to parse environment configuration response. The server returned invalid JSON.';
        let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
        console.error('Error parsing environment configuration:', jsonError);
        showEnvironmentLoadError({
          errorMessage: errorMsg,
          errorDetails: errorDetails
        });
        return; // Stop execution - error page is shown
      }
    } else {
      // Non-OK HTTP response
      let errorMessage = 'Failed to load environment configuration from the server.';
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      
      // Try to get error details from response body first
      let serverMessage = '';
      try {
        const errorData = await response.json();
        if (errorData.message) {
          serverMessage = errorData.message;
        }
        if (errorData.error && !serverMessage) {
          serverMessage = errorData.error;
        }
      } catch {
        // Ignore JSON parse errors for error responses
      }
      
      if (response.status === 403) {
        errorMessage = 'Access denied. The environment configuration endpoint is restricted.';
        errorDetails = serverMessage || 'The server refused to provide the configuration. Please ensure you are accessing the application from the correct domain.';
      } else if (response.status === 404) {
        // Check if it's a configuration file not found or endpoint not found
        if (serverMessage && serverMessage.includes('configuration file')) {
          errorMessage = 'Environment configuration file not found.';
          errorDetails = serverMessage;
        } else {
          errorMessage = 'Environment configuration endpoint not found.';
          errorDetails = serverMessage || 'The server endpoint /tms/v1/environment could not be found. Please contact your system administrator.';
        }
      } else if (response.status >= 500) {
        errorMessage = 'Server error while loading environment configuration.';
        errorDetails = serverMessage || `The server encountered an error (HTTP ${response.status}). Please try again later or contact your system administrator.`;
      } else {
        // Use server message if available for other status codes
        if (serverMessage) {
          errorDetails = serverMessage;
        }
      }
      
      console.error(`Failed to load runtime configuration: ${errorDetails}`);
      
      showEnvironmentLoadError({
        errorMessage: errorMessage,
        errorDetails: errorDetails
      });
      return; // Stop execution - error page is shown
    }
  } catch (error) {
    // Network errors, CORS errors, or other fetch failures
    const errorMessage = 'Unable to connect to the server to load environment configuration.';
    let errorDetails = error instanceof Error ? error.message : String(error);
    
    // Provide more helpful messages for common errors
    if (errorDetails.includes('Failed to fetch') || errorDetails.includes('NetworkError')) {
      errorDetails = 'Network connection failed. Please check your internet connection and ensure the server is accessible.';
    } else if (errorDetails.includes('CORS')) {
      errorDetails = 'Cross-origin request blocked. The server may not be configured to allow requests from this domain.';
    }
    
    console.error('Error loading runtime configuration:', error);
    showEnvironmentLoadError({
      errorMessage: errorMessage,
      errorDetails: errorDetails
    });
    return; // Stop execution - error page is shown
  }
}

async function loadRuntimeVersion(): Promise<void> {
  try {
    const endpointUrl = joinUrl(getPlanningToolApiRoot(), "version");
    const response = await fetch(endpointUrl);

    if (!response.ok) {
      console.warn(`Failed to load runtime version: HTTP ${response.status} ${response.statusText}`);
      return;
    }

    const data = await response.json();
    if (data && typeof data.version === "string" && data.version.trim()) {
      (environment as any).version = data.version;
      console.log('Runtime version loaded successfully');
    }
  } catch (error) {
    console.warn('Failed to load runtime version:', error);
  }
}

// Bootstrap application after loading configuration
loadRuntimeConfiguration().then(async () => {
  // Keep the actual compiled client identity independent of backend configuration.
  environment.frontendBuildId = FRONTEND_BUILD.id;
  // Only bootstrap if we didn't show an error page
  // Check if body has been cleared (which means error page is shown)
  if (document.body && document.body.children.length === 0) {
    console.log('Error page detected, skipping Angular bootstrap');
    return;
  }

  await loadRuntimeVersion();

  if (environment.production) {
    enableProdMode();
  }

  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(err => console.error('Error bootstrapping application:', err));
});
